import { useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function CorporateGifting() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", quantity: "", budget: "", occasion: "", branding: "", delivery_date: "", message: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name and phone are required");
    if (!form.consent) return toast.error("Please consent to being contacted");
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="container-x py-12 max-w-5xl" data-testid="corporate-gifting-page">
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Corporate Gifting</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">Gold that carries your name further</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">
        Bullion-backed gifts with your branding, live-rate pricing with no hidden metal markup, and certification
        on every piece. Diwali gifting, employee milestones, dealer meets — from 10 pieces to 10,000.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
        {[["Live-rate pricing", "Priced off the day's rate — never an inflated MRP"], ["BIS hallmarked", "Every gold piece certified, coins carry QR certificates"], ["Your branding", "Sleeves, cards and engraving with your identity"], ["GST invoice", "Clean input-credit paperwork for your accounts team"]].map(([t, d]) => (
          <div key={t} className="border border-stone-200 bg-white p-4" data-testid={`corp-usp-${t.split(" ")[0].toLowerCase()}`}>
            <p className="font-heading font-semibold text-sm">{t}</p>
            <p className="text-xs text-stone-500 mt-1">{d}</p>
          </div>
        ))}
      </div>
      {done ? (
        <div className="mt-10 border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="enquiry-success">
          <h2 className="font-heading text-2xl font-semibold text-emerald-900">Thank you — enquiry received.</h2>
          <p className="text-sm text-emerald-800 mt-2">Our gifting desk will reach you within one working day.</p>
        </div>
      ) : (
        <section className="mt-10 border border-stone-200 bg-white p-6 sm:p-8" data-testid="enquiry-form">
          <h2 className="font-heading font-semibold text-lg">Tell us about the occasion</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <div><Label>Your Name *</Label><Input value={form.name} onChange={set("name")} data-testid="enquiry-name" /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={set("company")} data-testid="enquiry-company" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="enquiry-email" /></div>
            <div><Label>Phone *</Label><Input value={form.phone} onChange={set("phone")} data-testid="enquiry-phone" /></div>
            <div><Label>Approx. quantity</Label><Input value={form.quantity} onChange={set("quantity")} placeholder="e.g. 250 pieces" data-testid="enquiry-quantity" /></div>
            <div><Label>Budget per piece</Label><Input value={form.budget} onChange={set("budget")} placeholder="e.g. ₹2,000–5,000" data-testid="enquiry-budget" /></div>
            <div><Label>Occasion</Label><Input value={form.occasion} onChange={set("occasion")} placeholder="Diwali, work anniversary…" data-testid="enquiry-occasion" /></div>
            <div><Label>Needed by</Label><Input type="date" value={form.delivery_date} onChange={set("delivery_date")} data-testid="enquiry-date" /></div>
            <div className="sm:col-span-2"><Label>Branding notes</Label><Input value={form.branding} onChange={set("branding")} placeholder="Logo on sleeve, engraved card…" data-testid="enquiry-branding" /></div>
            <div className="sm:col-span-2"><Label>Anything else</Label>
              <textarea value={form.message} onChange={set("message")} className="w-full h-20 border border-stone-200 rounded-sm px-3 py-2 text-sm" data-testid="enquiry-message" /></div>
            <label className="sm:col-span-2 flex items-start gap-2 text-xs text-stone-600">
              <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5" data-testid="enquiry-consent" />
              I agree to be contacted about this enquiry. (Transactional contact only — this is not marketing consent.)
            </label>
          </div>
          <Button onClick={submit} disabled={busy} className="mt-5 h-11 px-8 bg-stone-900 hover:bg-brand text-white btn-press" data-testid="enquiry-submit">
            {busy ? "Sending…" : "Send Enquiry"}
          </Button>
        </section>
      )}
    </div>
  );
}
